'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  isIllustratedFinish,
  VOUCHER_FINISHES,
  type VoucherFinish,
} from 'content/gifting/voucherConfig';
import {
  createMorpher,
  type Morpher,
} from 'components/gifting/visuals/morphShader';
import { warmCardShaders } from 'components/gifting/visuals/warmCardShaders';
import { usePrefersReducedMotion } from 'hooks/media/usePrefersReducedMotion';
import { voucherAssetUrl } from '../assets';
import { VoucherCard } from '../VoucherCard';
import type { CardContentProps } from './types';
import styles from './CardStage.module.css';

type Props = {
  finish: VoucherFinish;
  /** Props for each mounted card, so the outgoing one keeps its own labels. */
  cardProps: (finish: VoucherFinish) => CardContentProps;
  cardClassName?: string;
};

type Run = {
  from: VoucherFinish;
  to: VoucherFinish;
  raf: number;
  timer: number;
};

const MORPH_MS = 950;
const SLIDE_OUT_MS = 760;
// How long the incoming card may take to draw its first frame before we slide instead.
const READY_TIMEOUT_MS = 1000;

const ease = (x: number) => x * x * x * (x * (x * 6 - 15) + 10);

/** The card's own artwork canvas, once it has drawn (cards set data-gl="on"). */
const artOf = (layer: HTMLElement | undefined) => {
  const card = layer?.firstElementChild as HTMLElement | null;
  if (!card || card.dataset.gl !== 'on') return null;
  return card.querySelector(':scope > canvas') as HTMLCanvasElement | null;
};

/**
 * The morph's source for a card: its canvas, or for Prism the canvas seen
 * through its black plate (the SVG `[data-morph-plate]` wordmark path drawn as 2D), so the
 * plate dissolves with the artwork instead of fading in after it.
 */
const morphSource = (art: HTMLCanvasElement) => {
  const plate = art.parentElement?.querySelector<SVGSVGElement>(
    ':scope > svg[data-morph-plate]',
  );
  const word = plate?.querySelector('path');
  const ctx = word ? document.createElement('canvas').getContext('2d') : null;
  const mctx = ctx ? document.createElement('canvas').getContext('2d') : null;
  if (!plate || !word || !ctx || !mctx) return { src: art, sync: () => {} };
  const out = ctx.canvas;
  const mask = mctx.canvas;
  const shape = new Path2D(word.getAttribute('d') ?? '');

  // The wordmark as a white mask at the canvas's pixel size.
  const drawMask = (w: number, h: number) => {
    mask.width = w;
    mask.height = h;
    const k = w / (plate.viewBox.baseVal.width || w);
    mctx.scale(k, k);
    mctx.lineJoin = 'round';
    mctx.lineWidth = Number(word.getAttribute('stroke-width')) || 0;
    mctx.fillStyle = mctx.strokeStyle = '#fff';
    mctx.fill(shape);
    mctx.stroke(shape);
  };

  const sync = () => {
    const { width: w, height: h } = art;
    if (out.width !== w || out.height !== h) {
      out.width = w;
      out.height = h;
      drawMask(w, h);
    }
    // The artwork only inside the wordmark, black everywhere else.
    ctx.globalCompositeOperation = 'copy';
    ctx.drawImage(art, 0, 0);
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(mask, 0, 0);
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
  };
  return { src: out, sync };
};

/**
 * Card preview that transitions between finishes: the artwork morphs through a
 * noise front (card-transitions hand-off); without WebGL the cards slide, and
 * reduced motion gets a crossfade.
 */
export const CardStage = ({ finish, cardProps, cardClassName }: Props) => {
  const [layers, setLayers] = useState<VoucherFinish[]>([finish]);
  const layerEls = useRef(new Map<VoucherFinish, HTMLDivElement>());
  const morphHost = useRef<HTMLDivElement>(null);
  const morpher = useRef<Morpher | null | undefined>(undefined);
  const current = useRef(finish);
  const pending = useRef<{ from: VoucherFinish; to: VoucherFinish } | null>(
    null,
  );
  const run = useRef<Run | null>(null);
  const mounted = useRef(false);
  const reducedMotion = usePrefersReducedMotion();

  const setState = (f: VoucherFinish, s: string) => {
    const el = layerEls.current.get(f);
    if (el) el.dataset.s = s;
  };
  const drop = (f: VoucherFinish) =>
    setLayers((l) => l.filter((x) => x !== f || x === current.current));

  const hideMorph = () => {
    if (morphHost.current) morphHost.current.hidden = true;
  };

  const finishRun = () => {
    const r = run.current;
    if (!r) return;
    run.current = null;
    cancelAnimationFrame(r.raf);
    window.clearTimeout(r.timer);
    hideMorph();
    setState(r.to, 'on');
    setState(r.from, 'off');
    drop(r.from);
  };

  const slide = (from: VoucherFinish, to: VoucherFinish, r: Run) => {
    const el = layerEls.current.get(to);
    setState(from, 'out');
    if (el) {
      el.dataset.s = 'pre';
      void el.offsetWidth;
      el.dataset.s = 'on';
    }
    r.timer = window.setTimeout(() => {
      if (run.current !== r) return;
      run.current = null;
      setState(from, 'off');
      drop(from);
    }, SLIDE_OUT_MS);
  };

  const morph = (
    from: VoucherFinish,
    to: VoucherFinish,
    r: Run,
    A: HTMLCanvasElement,
    B: HTMLCanvasElement,
  ) => {
    const m = morpher.current;
    const host = morphHost.current;
    const card = layerEls.current.get(to)?.firstElementChild as
      | HTMLElement
      | undefined;
    if (!m || !host || !card) return slide(from, to, r);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = card.offsetWidth;
    const h = card.offsetHeight;
    m.resize(
      Math.max(2, Math.round(w * dpr)),
      Math.max(2, Math.round(h * dpr)),
    );
    const cs = getComputedStyle(card);
    Object.assign(m.canvas.style, {
      width: `${w}px`,
      height: `${h}px`,
      borderRadius: cs.borderRadius,
      boxShadow: cs.boxShadow,
    });
    const a = morphSource(A);
    const b = morphSource(B);
    let labelsIn = false;
    const t0 = performance.now();
    const step = (now: number) => {
      if (run.current !== r) return;
      const t = Math.min(1, (now - t0) / MORPH_MS);
      a.sync();
      b.sync();
      if (!m.frame(a.src, b.src, ease(t), now / 1000)) return finishRun();
      if (!labelsIn && t > 0.34) {
        labelsIn = true;
        setState(to, 'min');
      }
      if (t < 1) r.raf = requestAnimationFrame(step);
      else finishRun();
    };
    step(t0);
    host.hidden = false;
    setState(from, 'mout');
    setState(to, 'mset');
  };

  const start = (from: VoucherFinish, to: VoucherFinish) => {
    const r: Run = { from, to, raf: 0, timer: 0 };
    run.current = r;
    const dir =
      VOUCHER_FINISHES.indexOf(to) >= VOUCHER_FINISHES.indexOf(from) ? 1 : -1;
    layerEls.current.forEach((el) => el.style.setProperty('--dir', `${dir}`));

    if (reducedMotion) return slide(from, to, r);
    if (morpher.current === undefined) {
      morpher.current = createMorpher();
      if (morpher.current && morphHost.current)
        morphHost.current.appendChild(morpher.current.canvas);
    }
    if (!morpher.current) return slide(from, to, r);

    // Wait for the incoming card's first frame; the outgoing one must have drawn too.
    const A = artOf(layerEls.current.get(from));
    if (!A) return slide(from, to, r);
    const t0 = performance.now();
    const wait = () => {
      if (run.current !== r) return;
      const B = artOf(layerEls.current.get(to));
      if (B) return morph(from, to, r, A, B);
      if (performance.now() - t0 > READY_TIMEOUT_MS) return slide(from, to, r);
      r.raf = requestAnimationFrame(wait);
    };
    r.raf = requestAnimationFrame(wait);
  };

  useEffect(() => {
    if (finish === current.current) return;
    finishRun();
    const from = current.current;
    current.current = finish;
    pending.current = { from, to: finish };
    setLayers([from, finish]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finish]);

  useLayoutEffect(() => {
    mounted.current = true;
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    start(p.from, p.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers]);

  // Once idle: warm the illustration cache and the shader cache, so a first
  // switch to a design neither hitches nor falls back to sliding.
  useEffect(() => {
    let cancelWarm: (() => void) | undefined;
    const idle =
      window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 600));
    const id = idle(() => {
      VOUCHER_FINISHES.filter(isIllustratedFinish).forEach((f) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = voucherAssetUrl(`illus-${f}.webp`);
      });
      cancelWarm = warmCardShaders();
    });
    return () => {
      cancelWarm?.();
      if (window.cancelIdleCallback) window.cancelIdleCallback(id as number);
      else clearTimeout(id as unknown as number);
    };
  }, []);

  useEffect(
    () => () => {
      const r = run.current;
      if (r) {
        cancelAnimationFrame(r.raf);
        window.clearTimeout(r.timer);
      }
      morpher.current?.dispose();
    },
    [],
  );

  return (
    <div className={styles.stage}>
      <div ref={morphHost} className={styles.morph} hidden aria-hidden="true" />
      {layers.map((f) => (
        <div
          key={f}
          className={styles.layer}
          ref={(el) => {
            if (!el) {
              layerEls.current.delete(f);
              return;
            }
            if (!el.dataset.s) el.dataset.s = mounted.current ? 'pre' : 'on';
            layerEls.current.set(f, el);
          }}
        >
          <VoucherCard finish={f} {...cardProps(f)} className={cardClassName} />
        </div>
      ))}
    </div>
  );
};
